import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useMemo } from 'react';
import { sanitizeHtml } from '@/shared/lib';
import { radius } from '@/shared/theme';

interface RichTextProps {
  /** HTML written by a user; always sanitised before it reaches the DOM. */
  html: string;
  sx?: SxProps<Theme>;
}

/** Renders a ticket description or comment body with the editor's formatting. */
export function RichText({ html, sx }: RichTextProps) {
  const clean = useMemo(() => sanitizeHtml(html), [html]);

  return (
    <Box
      sx={[
        (theme) => ({
          fontSize: 14,
          lineHeight: 1.6,
          overflowWrap: 'anywhere',
          '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
          '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
          '& li': { mb: 0.5 },
          '& h1, & h2, & h3': { fontSize: 16, fontWeight: 800, m: 0, mb: 1 },
          '& a': { color: theme.vars.palette.md.primary },
          '& img': { maxWidth: '100%', height: 'auto', borderRadius: radius.sm, my: 1 },
          '& code': {
            fontFamily: 'ui-monospace, monospace',
            fontSize: 12.5,
            background: theme.vars.palette.md.panel3,
            borderRadius: radius.xs,
            px: '5px',
            py: '2px',
          },
          '& pre': {
            background: theme.vars.palette.md.panel3,
            borderRadius: radius.sm,
            p: 1.5,
            overflowX: 'auto',
          },
          '& blockquote': {
            m: 0,
            mb: 1,
            pl: 1.5,
            borderLeft: `3px solid ${theme.vars.palette.md.border}`,
            color: theme.vars.palette.md.muted,
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitised above; see sanitize-html.ts
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
